import { Link } from "react-router-dom";
import { SEO } from "@/components/SEO";
import { CONTACT_EMAIL } from "@/lib/brand";
import { useSiteLanguage } from "@/lib/useSiteLanguage";
import { legalChromeCopy, privacyBodyCopy } from "@/lib/siteCopy";

export function PrivacyPage() {
  const lang = useSiteLanguage();
  const t = legalChromeCopy[lang];
  const h = t.privacyHeadings;
  const b = privacyBodyCopy[lang];
  const domain = "devdotcom.org";

  return (
    <>
      <SEO
        title="Privacy Policy | DevDotCom"
        description="How DevDotCom collects, uses, and protects your information."
        keywords="privacy policy, DevDotCom privacy, data protection"
      />
      <section className="bg-background px-5 pb-20 pt-28 sm:px-6 sm:pb-24 sm:pt-32 lg:px-10">
        <div className="mx-auto max-w-3xl">
          <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
            {t.eyebrow}
          </p>
          <h1 className="mt-3 text-4xl font-bold tracking-tight sm:text-5xl">
            {t.privacyTitle}
          </h1>
          <p className="mt-4 text-muted-foreground">{t.lastUpdated}</p>

          <div className="prose-neutral mt-10 space-y-8 text-[15px] leading-relaxed text-foreground/90">
            <section>
              <h2 className="text-xl font-semibold tracking-tight">{h.overview}</h2>
              <p className="mt-2 text-muted-foreground">
                {b.overview.split("{domain}")[0]}
                <span className="text-foreground">{domain}</span>
                {b.overview.split("{domain}")[1]}
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold tracking-tight">
                {h.collect}
              </h2>
              <ul className="mt-2 list-disc space-y-2 pl-5 text-muted-foreground">
                {b.collect.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold tracking-tight">
                {h.use}
              </h2>
              <ul className="mt-2 list-disc space-y-2 pl-5 text-muted-foreground">
                {b.use.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold tracking-tight">{h.sharing}</h2>
              <p className="mt-2 text-muted-foreground">{b.sharing}</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold tracking-tight">{h.retention}</h2>
              <p className="mt-2 text-muted-foreground">{b.retention}</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold tracking-tight">{h.choices}</h2>
              <p className="mt-2 text-muted-foreground">
                {b.choicesBefore}{" "}
                <a
                  href={`mailto:${CONTACT_EMAIL}`}
                  className="font-medium text-foreground underline underline-offset-4"
                >
                  {CONTACT_EMAIL}
                </a>
                {b.choicesAfter}
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold tracking-tight">{h.contact}</h2>
              <p className="mt-2 text-muted-foreground">
                {b.contactBefore}{" "}
                <a
                  href={`mailto:${CONTACT_EMAIL}`}
                  className="font-medium text-foreground underline underline-offset-4"
                >
                  {CONTACT_EMAIL}
                </a>
                . {b.contactOr}{" "}
                <Link
                  to="/contact"
                  className="font-medium text-foreground underline underline-offset-4"
                >
                  {t.contactLink}
                </Link>{" "}
                {b.contactAfter}
              </p>
            </section>
          </div>
        </div>
      </section>
    </>
  );
}
